// @flow

import * as React from 'react';
import styled from '@emotion/styled';
import { padding } from 'polished';

import FilterButton from 'app/components/commons/FilterButton';
import FilterGroup from 'app/components/commons/FilterGroup';
import Rows from 'app/components/layout/Rows';
import LeaderboardEntityFilter from './LeaderboardEntityFilter';
import Icon from '../commons/Icon';

type Props = {
  racingType?: string,
  countries?: Array<Object>,
  players?: Array<Object>,
  filteredCountryId?: number,
  isRacing?: boolean,
  onCountryChange: number => void,
  onSelectEntity: (entity: Object) => void,
  scheme?: 'light' | 'dark',
  selectedPlayers?: Array<string>,
  selectedTeams?: Array<string>
};

const FiltersDiv = styled.div(
  {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'transparent'
  },
  ({ theme }) => ({
    ...padding(theme.spacing.small, 0),
    borderBottom: '1px solid rgba(100, 100, 100, 0.25)'
  })
);

const StyledFilterButton = styled(FilterButton)({
  height: 30,
  flexGrow: 1
});

const CountryFlag = styled.img({
  width: 18,
  height: 18,
  marginRight: 4,
  borderRadius: '100%'
});

const Pagination = styled.div(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  paddingTop: 4,
  fontSize: theme.fontSize.xxsmall
}));

const Button = styled.button(({ disabled, theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: disabled ? theme.color.disabled : 'inherit'
}));

const LIMIT = 12;

const LeaderBoardFilterBarContent: React.FC<Props> = ({
  countries,
  players,
  racingType,
  filteredCountryId,
  isRacing,
  onCountryChange,
  onSelectEntity,
  scheme,
  selectedPlayers,
  selectedTeams
}: Props) => {
  const [startPos, setStartPos] = React.useState(0);
  const lastPos = players.length;

  const goPrev = () => {
    setStartPos(Math.max(startPos - LIMIT, 0));
  };

  const goNext = () => {
    setStartPos(startPos + LIMIT > lastPos ? startPos + LIMIT - lastPos : startPos + LIMIT);
  };

  const visiblePlayers = players.slice(startPos, startPos + LIMIT);

  return (
    <FiltersDiv>
      {visiblePlayers.length > 0 && (
        <FilterGroup label={`Filter by ${isRacing && racingType ? racingType : 'Player'}`}>
          <Rows mobile={1} tabletPortrait={2} gutter={3}>
            {visiblePlayers.map(
              ({ id, isTeam, dropped, name, popularity, score, statusId, selected }) => (
                <LeaderboardEntityFilter
                  id={id}
                  key={id}
                  dropped={dropped}
                  name={name}
                  onSelectEntity={onSelectEntity}
                  popularity={popularity}
                  score={score}
                  isTeam={isTeam}
                  statusId={statusId}
                  isSelected={isTeam ? selectedTeams.includes(id) : selectedPlayers.includes(id)}
                  playerSubmitted={selected}
                />
              )
            )}
          </Rows>
          <Pagination>
            <Button type="button" onClick={goPrev} disabled={startPos === 0}>
              <Icon type="icon-left-arrow" />
              <span style={{ paddingLeft: 4 }}>PREVIOUS</span>
            </Button>
            <Button type="button" onClick={goNext} disabled={startPos + LIMIT >= lastPos}>
              <span style={{ paddingRight: 4 }}>MORE</span>
              <Icon type="icon-right-arrow" />
            </Button>
          </Pagination>
        </FilterGroup>
      )}
      {countries.length > 1 && (
        <FilterGroup label="Filter by Location">
          <Rows mobile={4} tabletPortrait={6}>
            {countries.map(country => (
              <StyledFilterButton
                key={country.id}
                id={country.id}
                onClick={onCountryChange}
                selected={country.id === filteredCountryId}
                icon={<CountryFlag src={country.flag} alt="" />}
                scheme={scheme}
              >
                {country.abbr}
              </StyledFilterButton>
            ))}
          </Rows>
        </FilterGroup>
      )}
    </FiltersDiv>
  );
};

LeaderBoardFilterBarContent.defaultProps = {
  countries: [],
  racingType: undefined,
  players: [],
  isRacing: false,
  filteredCountryId: undefined,
  selectedPlayers: [],
  selectedTeams: [],
  scheme: 'light'
};

export default LeaderBoardFilterBarContent;
